import React, { useState } from 'react';
import { Modal, Badge } from 'react-bootstrap';
import { IconBook2 } from '@tabler/icons-react';
import ReactMarkdown from 'react-markdown';

/**
 * CAPABILITY_GUIDES - Conteúdo dos guias por capability key.
 *
 * Cada capability pode ter um guia explicativo em Markdown.
 * Ao adicionar uma nova capability, basta incluir a key aqui.
 */
const CAPABILITY_GUIDES = {
  email_monitor: {
    title: 'Como funciona o Leitor de Email',
    content: `
### O que faz?

Quando a conversa chega neste nó, o agente **busca emails recentes** no Gmail conectado, **analisa o conteúdo** com IA e pode **executar ações** como responder, encaminhar resumos via WhatsApp ou marcar como lido.

### Como funciona o fluxo?

1. **O cliente envia uma mensagem** no WhatsApp (gatilho)
2. O fluxo do agente avança até chegar no nó **Leitor de Email**
3. O agente busca emails no Gmail com os **filtros configurados** (período, remetente, assunto)
4. A IA **analisa** cada email encontrado
5. O agente executa as **ações configuradas**:
   - Responde ao cliente no WhatsApp com o resumo
   - Envia resumo para outro número de WhatsApp (se configurado)
   - Responde emails automaticamente no Gmail (se ativado)
   - Marca emails como lidos (se ativado)
6. O fluxo continua para o **próximo nó**

### Exemplo prático

Imagine que você é dono de uma clínica e quer que seu agente verifique emails quando você pedir:

\`\`\`
[Início] → [IA: "Olá! Quer que eu verifique seus emails?"]
         → [Leitor de Email: últimas 24h, resumo via WhatsApp]
         → [Fim]
\`\`\`

1. Você manda **"verificar emails"** no WhatsApp do agente
2. O agente busca emails das últimas 24h
3. Responde com algo como:
   > 📧 **2 emails novos:**
   > 1. **De:** cliente@email.com — Pedido de orçamento para reforma
   > 2. **De:** fornecedor@email.com — Confirmação de entrega sexta-feira

### Filtros disponíveis

| Filtro | O que faz |
|--------|-----------|
| **Período** | Busca emails das últimas 24h, 48h, 72h ou 7 dias |
| **Apenas não lidos** | Ignora emails já lidos |
| **Remetente** | Filtra por email(s) específico(s) |
| **Assunto** | Busca por palavra no assunto |

### Ações automáticas

| Ação | O que faz |
|------|-----------|
| **Resposta automática** | A IA responde emails no Gmail seguindo suas instruções |
| **Encaminhar via WhatsApp** | Envia resumo dos emails para um número de WhatsApp |
| **Marcar como lido** | Marca emails processados como lidos no Gmail |

### Importante

- O nó **só executa quando o fluxo chega nele** — não roda sozinho em segundo plano
- Emails já processados são **automaticamente ignorados** (não repete leitura)
- Requer **integração Google Workspace** ativa com permissão de Gmail
    `,
  },

  sheets_reader: {
    title: 'Como funciona o Leitor de Planilha',
    content: `
### O que faz?

Quando a conversa chega neste nó, o agente **lê dados de uma planilha Google Sheets** configurada, **analisa com IA** conforme suas instruções e **conversa com o usuário** sobre os dados. Pode enviar resumos para contatos via WhatsApp.

### Como funciona o fluxo?

1. **O cliente envia uma mensagem** no WhatsApp (gatilho)
2. O fluxo avança até o nó **Leitor de Planilha**
3. O agente lê a **planilha e aba configuradas**
4. A IA **analisa** os dados seguindo suas instruções
5. O agente **apresenta os resultados** ao usuário
6. O usuário pode **fazer perguntas**, pedir **filtros** ou **resumos**
7. Se solicitado, o agente **envia resumos** para contatos via WhatsApp

### Exemplo prático

Imagine que você tem uma planilha de vendas e quer que o agente analise:

1. Você manda **"como estão as vendas do mês?"**
2. O agente lê a planilha e responde:
   > Encontrei 150 registros de vendas em março.
   > Total: R$ 450.000,00
   > Top 3 clientes: João (R$ 80mil), Maria (R$ 65mil), Carlos (R$ 50mil)
3. Você pergunta: **"quem comprou acima de 100mil?"**
4. O agente filtra e responde com a lista
5. Você pede: **"envia esse resumo pra Camila"**
6. O agente confirma e envia via WhatsApp

### Importante

- O nó **só executa quando o fluxo chega nele** — não roda sozinho
- Requer **integração Google Sheets** ativa
- Para enviar resumos via WhatsApp, configure **Contatos do Agente** nas configurações avançadas
    `,
  },
};

/**
 * CapabilityGuideModal — Componente reutilizável de guia para capabilities.
 *
 * Renderiza um badge "Guia" que ao clicar abre um modal com explicação
 * detalhada de como a capability funciona.
 *
 * Props:
 *   capabilityKey (string) — Key da capability (ex: 'email_monitor')
 *
 * Uso:
 *   <CapabilityGuideModal capabilityKey="email_monitor" />
 *
 * Para adicionar guia a uma nova capability, basta adicionar uma entrada
 * no objeto CAPABILITY_GUIDES acima.
 */
const CapabilityGuideModal = ({ capabilityKey }) => {
  const [show, setShow] = useState(false);

  const guide = CAPABILITY_GUIDES[capabilityKey];

  // Não renderiza nada se não há guia para esta capability
  if (!guide) return null;

  return (
    <>
      <Badge
        bg=""
        className="afc-guide-badge"
        onClick={() => setShow(true)}
        style={{
          cursor: 'pointer',
          background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
          color: '#fff',
          fontSize: '0.7rem',
          fontWeight: 600,
          padding: '4px 8px',
          borderRadius: '6px',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          marginLeft: '8px',
          verticalAlign: 'middle',
        }}
      >
        <IconBook2 size={13} stroke={2} />
        Guia
      </Badge>

      <Modal
        show={show}
        onHide={() => setShow(false)}
        size="lg"
        centered
        className="afc-guide-modal"
      >
        <Modal.Header closeButton style={{ borderBottom: '1px solid #e5e7eb' }}>
          <Modal.Title style={{ fontSize: '1.1rem', fontWeight: 600 }}>
            {guide.title}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body
          style={{
            maxHeight: '70vh',
            overflowY: 'auto',
            padding: '1.5rem',
            fontSize: '0.9rem',
            lineHeight: '1.65',
          }}
        >
          <ReactMarkdown>{guide.content}</ReactMarkdown>
        </Modal.Body>
      </Modal>
    </>
  );
};

export { CAPABILITY_GUIDES };
export default CapabilityGuideModal;
